// Support portal — file a problem report (POST /tenants/:id/support/tickets,
// multipart so screenshots / recordings / transcripts ride along) and read them
// back: a tenant admin lists their own tenant, platform staff list every tenant.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { SupportTicket } from "@csai/core";
import { useApi } from "./api";

export type SupportTicketRow = Omit<SupportTicket, "createdAt" | "closedAt"> & {
  createdAt: string;
  closedAt?: string;
};

export type SupportTicketDraft = {
  title: string;
  steps: string;
  expected: string;
  actual: string;
  chatSessionId: string;
  draftId: string;
  pageUrl: string;
};

export const SUPPORT_KEY = ["support", "tickets"] as const;

export function useSubmitSupportTicket(): UseMutationResult<
  SupportTicketRow,
  Error,
  { tenantId: string; draft: SupportTicketDraft; files: File[] }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenantId, draft, files }) => {
      const body = new FormData();
      for (const [k, v] of Object.entries(draft)) body.append(k, v);
      for (const f of files) body.append("attachments", f);
      return await api<SupportTicketRow>(
        `/tenants/${encodeURIComponent(tenantId)}/support/tickets`,
        { method: "POST", body }
      );
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: SUPPORT_KEY }),
  });
}

/** tenantId null → every tenant (platform reach); otherwise that tenant only. */
export function useSupportTickets(
  tenantId: string | null
): UseQueryResult<SupportTicketRow[], Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...SUPPORT_KEY, tenantId ?? "all"],
    queryFn: async () => {
      const path =
        tenantId === null
          ? "/support/tickets"
          : `/tenants/${encodeURIComponent(tenantId)}/support/tickets`;
      const res = await api<{ tickets: SupportTicketRow[] }>(path);
      return res.tickets;
    },
  });
}

export function useCloseSupportTicket(): UseMutationResult<
  SupportTicketRow,
  Error,
  { tenantId: string; ticketId: string }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, ticketId }) =>
      api<SupportTicketRow>(
        `/tenants/${encodeURIComponent(tenantId)}/support/tickets/${encodeURIComponent(ticketId)}/close`,
        { method: "POST" }
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: SUPPORT_KEY }),
  });
}

/** A 5-minute signed link to one attachment; fetched on click, never listed. */
export function useAttachmentUrl(): (tenantId: string, ticketId: string, n: number) => Promise<string> {
  const api = useApi("admin");
  return async (tenantId, ticketId, n) => {
    const res = await api<{ url: string }>(
      `/tenants/${encodeURIComponent(tenantId)}/support/tickets/${encodeURIComponent(ticketId)}/attachments/${n}`
    );
    return res.url;
  };
}
